import { useEffect, useMemo } from 'react';
import { Page } from '@Components/Page/Page';
import { FullPageSpinner } from '@Components/Spinner/Spinner';
import { useAppSelector } from '@src/redux';
import { useSetError } from '@src/redux/modals';
import { isAdminSelector } from '@src/redux/user';
import { useExperimentFlag } from '@src/utils/experiment-context';
import { useCompanyInfo } from '@src/utils/hooks/useCompanyInfo';
import { useTabbedNav } from '@src/utils/hooks/useNav';
import { AmcMediaMix } from '@src/Pages/AmcMediaMix';
import { AmcGeo } from '@src/Pages/AmcGeo';
import { AmcTableau } from '@src/Pages/AmcTableau';
import { AmcPathingAnalysis } from '@src/Pages/AmcPathingAnalysis';
import { AudiencePenetration } from '@src/Pages/AudiencePenetration';
import { CategoryPathing } from '@src/Pages/CategoryPathing';
import { AmcNewToBrand } from '@src/Pages/AmcNewToBrand';
import { AmcNtbLtv } from '@src/Pages/AmcNtbLtv';
import { useAmcReports } from './utils/useAmcReports';
import {
  AMC_INSIGHTS_TABS,
  AMC_TEST_INSTANCE_ID,
  AmcInsightsTab,
  AmcInsightsTabKey,
  AUDIENCE_PENETRATION_KEY,
  CATEGORY_PATHING_KEY,
  MEDIA_MIX_KEY,
  GEO_KEY,
  NEW_TO_BRAND_KEY,
  NTB_LTV_KEY,
  PAGE_TYPE,
  PATHING_ANALYSIS_KEY,
  TABLEAU_KEY
} from './amc-insights-constants';

const AMC_BASE = 'amc-insights';

interface AmcInsightsBodyProps {
  isLoading: boolean;
  tab: AmcInsightsTabKey;
  categoryPathingEnabled: boolean;
  amcInstanceId?: string;
}

const AmcInsightsBody = ({
  isLoading,
  tab,
  categoryPathingEnabled,
  amcInstanceId
}: AmcInsightsBodyProps) => {
  if (isLoading) return <FullPageSpinner />;
  if (tab === CATEGORY_PATHING_KEY && categoryPathingEnabled) {
    return <CategoryPathing amcInstanceId={amcInstanceId} />;
  }
  if (tab === AUDIENCE_PENETRATION_KEY) {
    return <AudiencePenetration amcInstanceId={amcInstanceId} />;
  }
  if (tab === MEDIA_MIX_KEY) {
    return <AmcMediaMix amcInstanceId={amcInstanceId} />;
  }
  if (tab === GEO_KEY) {
    return <AmcGeo amcInstanceId={amcInstanceId} />;
  }
  if (tab === TABLEAU_KEY) {
    return <AmcTableau />;
  }
  if (tab === NEW_TO_BRAND_KEY) {
    return <AmcNewToBrand amcInstanceId={amcInstanceId} />;
  }
  if (tab === PATHING_ANALYSIS_KEY) {
    return <AmcPathingAnalysis amcInstanceId={amcInstanceId} />;
  }
  if (tab === NTB_LTV_KEY) {
    return <AmcNtbLtv amcInstanceId={amcInstanceId} />;
  }
  return undefined;
};

export const AmcInsights = () => {
  const { cid, amc_instance_id: companyAmcInstanceId } = useCompanyInfo();
  const amcInstanceId = cid === 'tinuititest' ? AMC_TEST_INSTANCE_ID : companyAmcInstanceId;
  const isAdmin = useAppSelector(isAdminSelector);
  const categoryPathingEnabled = Boolean(useExperimentFlag('enableAmcPathingAnalysis'));

  const { data: reports, isLoading, error } = useAmcReports(amcInstanceId);
  const setError = useSetError();

  useEffect(() => {
    if (error) {
      setError({
        title: 'Error fetching AMC data',
        message: `Failed to load AMC reports: ${error.message}`,
        reportError: error
      });
    }
  }, [error, setError]);

  // Mirror bpm-frontend's tab visibility so the shared shell looks continuous: report-gated tabs
  // appear only when the client has that report, Tableau is admin-only, and Category Pathing is
  // gated by the `enableAmcPathingAnalysis` experiment. `isAdmin`/`categoryPathingEnabled` are
  // known synchronously, but `reports` isn't — returning early while it's still loading holds
  // Tableau and Category Pathing back too, so every tab the client can see appears together in
  // one pass instead of Tableau/Category Pathing rendering first and the rest popping in after.
  const availableTabs = useMemo(() => {
    if (isLoading) return [];
    return AMC_INSIGHTS_TABS.filter((t: AmcInsightsTab) => {
      if (t.key === CATEGORY_PATHING_KEY) return categoryPathingEnabled;
      if (t.adminOnly) return isAdmin;
      return Boolean(reports?.some(r => r.reportType === t.reportType));
    });
  }, [isLoading, reports, isAdmin, categoryPathingEnabled]);

  const { tab, goToTab } = useTabbedNav<AmcInsightsTabKey>({
    baseURL: AMC_BASE,
    defaultKey: TABLEAU_KEY,
    navs: availableTabs.map(t => t.key)
  });

  return (
    <Page
      title='AMC Insights'
      pageType={PAGE_TYPE}
      isLoading={isLoading}
      navs={availableTabs.map(({ key, label }) => ({ key, label }))}
      selectedNav={tab}
      onNav={key => goToTab(key as AmcInsightsTabKey)}
    >
      <AmcInsightsBody
        isLoading={isLoading}
        tab={tab}
        categoryPathingEnabled={categoryPathingEnabled}
        amcInstanceId={amcInstanceId}
      />
    </Page>
  );
};
